'use client';

import { Fragment, useEffect, useState } from 'react';

import { Dialog, Transition } from '@headlessui/react';
import { QrCodeGenerator } from '@northladder/design-system';
import { Text, useTranslations } from '@northladder/i18n';

import { useNavRouter } from '../hooks';

/**
 * -----------------------------------------------------------------------------
 * This component displays a popup with a QR code for the current web application link.
 * The popup is only shown on desktop devices (not mobile/tablet). The QR code color
 * adapts to the current theme (white for dark mode, black for light mode).
 * The popup includes localized title, subtitle, description, and notice text.
 */
export function ScanForMobileWebLinkPopup() {
  const t = useTranslations('qrCodeScanScreen');

  // Gets the base host URL from environment variables.
  const baseURL = process.env.NEXT_PUBLIC_BASE_HOST_URL || '';

  // Returns the full route URL for the QR code value.
  const { getRouteURL } = useNavRouter();
  const qrCodeValue = getRouteURL(baseURL);

  /**
   * Determines if the current device is a mobile or tablet based on window width.
   * Returns true for devices with width < 1025px.
   */
  function isMobileOrTablet() {
    if (typeof window === 'undefined') return false;

    const width = window.innerWidth;

    // Typical breakpoints: mobile < 768, tablet < 1025
    return width < 1025;
  }

  const [shouldShow, setShouldShow] = useState(() => !isMobileOrTablet());

  // Adds a window resize event listener to dynamically show/hide the popup based on device width.
  useEffect(() => {
    function handleResize() {
      if (isMobileOrTablet()) {
        setShouldShow(false);
      } else {
        setShouldShow(true);
      }
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  /**
   * Determines if the current theme is dark by checking the 'dark' class on <html>.
   */
  const isDark =
    typeof window !== 'undefined' &&
    document.documentElement.classList.contains('dark');

  return (
    <Transition appear as={Fragment} show={shouldShow}>
      <Dialog as="div" className="relative z-[100]" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/75" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="
                  w-full max-w-sm transform overflow-hidden rounded-2xl bg-white p-6
                  text-start align-middle shadow-xl transition-all rtl:text-end
                  dark:bg-gray-600
                "
              >
                <div className="mx-auto flex max-w-4xl flex-col items-center py-8">
                  <QrCodeGenerator
                    fgColor={isDark ? 'white' : 'black'}
                    level="L"
                    value={qrCodeValue}
                  />
                  <h1
                    className="
                      drop-shadow-xs text-body-text pt-12 text-center text-4xl
                      font-bold
                    "
                  >
                    {t('title')}
                  </h1>
                  <p
                    className="
                      text-primary-900 dark:text-primary-200 max-w-xl pt-2 text-center
                      text-base font-normal
                    "
                  >
                    {t('subtitle')}
                  </p>
                </div>
                <div
                  className="
                    m-auto flex max-w-xs flex-col items-center justify-center gap-2
                    pb-10 text-center text-sm font-normal leading-5
                  "
                >
                  <p className="flex flex-col text-center text-gray-600 dark:text-gray-200">
                    {t('description')}
                  </p>
                  <hr className="my-2 w-full border-gray-400/10" />
                  <Text
                    className="text-primary-600 dark:text-primary-400 text-xs"
                    text={t('notice')}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
